<template>
  <!-- Teleported for the same reason as the GoHighLevel guide: both callers open
       it from a transformed modal dialog, so a drawer left inside would anchor to
       the modal instead of the viewport. -->
  <Teleport to="body">
    <div class="dialpad-help-drawer relative z-[1040]">
      <Drawer
        :title="title"
        :aria-label="title"
        no-footer
        data-cy="dialpad-help-drawer"
        @hide-drawer="emit('close')"
      >
        <template #title>
          <h2
            class="mb-0 pr-10 text-base font-semibold text-gray-900"
            data-cy="dialpad-help-title"
          >
            {{ title }}
          </h2>
        </template>
        <div
          v-if="topic === 'recording'"
          class="dialpad-guide-body px-4 pt-2 pb-4 text-gray-700"
          data-cy="dialpad-recording-guide"
        >
          <p class="dialpad-guide-heading">Where it lives in Dialpad</p>
          <ol class="dialpad-guide-list">
            <li>Log into Dialpad as an admin.</li>
            <li>
              Open <strong>Admin Settings</strong>, then
              <strong>Office</strong>, then <strong>Office Settings</strong>.
            </li>
            <li>
              Under <strong>Office-wide settings</strong>, then
              <strong>Making Calls</strong>, tick
              <strong>Enable Automatic Call Recordings</strong>.
            </li>
            <li>
              Choose
              <strong>Automatically record all calls in the office</strong>.
            </li>
          </ol>
          <p class="dialpad-guide-heading mt-4">Why this matters</p>
          <p class="mb-0">
            Dialpad only keeps a recording of a call it recorded, so without
            this most calls have no recording to play in Phone Calls. Dialpad
            plays no recording notice to callers when this is on, so check the
            consent rules where you work.
          </p>
        </div>
        <div
          v-else
          class="dialpad-guide-body px-4 pt-2 pb-4 text-gray-700"
          data-cy="dialpad-transcript-guide"
        >
          <p class="dialpad-guide-heading">Where it lives in Dialpad</p>
          <ol class="dialpad-guide-list">
            <li>Log into Dialpad as an admin.</li>
            <li>
              Open <strong>Admin Settings</strong>, then
              <strong>Office</strong>, then <strong>AI Settings</strong>.
            </li>
            <li>Turn on <strong>Allow AI to be used office-wide</strong>.</li>
            <li>
              Under <strong>Default preferences</strong>, keep
              <strong>Automatically start AI for calls</strong> ticked, so new
              users get transcripts too.
            </li>
          </ol>
          <p class="dialpad-guide-heading mt-4">Why this matters</p>
          <ol class="dialpad-guide-list">
            <li>
              A call's transcript comes from Dialpad AI, not from recording.
              Sera fetches it about 2 minutes after the call ends, so it shows
              within 2-3 minutes.
            </li>
            <li>A voicemail's transcript comes with the voicemail.</li>
          </ol>
        </div>
      </Drawer>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Drawer from "@/components/Seranova/Drawer/Drawer.vue";

export type DialpadHelpTopic = "recording" | "transcript";

const TITLES: Record<DialpadHelpTopic, string> = {
  recording: "Turning on call recording in Dialpad",
  transcript: "Turning on transcripts in Dialpad"
};

const props = defineProps<{ topic: DialpadHelpTopic }>();
const emit = defineEmits<{ (e: "close"): void }>();

const title = computed(() => TITLES[props.topic]);
</script>

<style lang="scss">
// Same look as the GoHighLevel guide. Not scoped, because the drawer is teleported
// out of this component.

// Flush to the edge and full height, like the GoHighLevel guide.
.dialpad-help-drawer .drawer.grid {
  margin: 0 !important;
  max-height: 100dvh !important;
  border-radius: 0 !important;
}

.dialpad-guide-body {
  font-size: 0.8125rem;
  line-height: 1.5;
}

.dialpad-guide-heading {
  margin: 0 0 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

// List markers are reset by Tailwind's preflight, so the steps count themselves.
.dialpad-guide-list {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: dialpad-step;

  li {
    display: block;
    position: relative;
    padding-left: 1.5rem;
    counter-increment: dialpad-step;

    &::before {
      position: absolute;
      left: 0.25rem;
      color: #6b7280;
      content: counter(dialpad-step) ".";
    }
  }

  li + li {
    margin-top: 0.25rem;
  }
}
</style>
